import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import FeatherIcon from '../../../components/common/FeatherIcon';
import MarkdownText from '../../../components/common/MarkdownText';
import { stepsDuration, tableFromMessage } from './agentModel';

// create_alert_rule / update_alert_rule approvals open as the alert rule editor, so the person can
// change the rule and its wording before approving. Loaded only when such a card appears.
const ALERT_RULE_TOOLS = ['create_alert_rule', 'update_alert_rule'];
const AlertRuleApproval = lazy(() => import('../../admin/alert-configuration/ai/AlertRuleApproval'));
// create_api_key / rotate_api_key approvals open as an editable key card that makes the call itself,
// in the person's session, so the new key's secret is shown once there and never stored in the chat.
const API_KEY_TOOLS = ['create_api_key', 'rotate_api_key'];
const ApiKeyApproval = lazy(() => import('../../admin/api-keys/ApiKeyApproval'));

const PALETTE = ['#7B68EE', '#46D9C5', '#fbbf24', '#FF6B8E', '#5B8FF9', '#F0A0D8', '#34d399', '#FF9247'];

/**
 * An ECharts block for a `chart` answer. Draws with the page's global echarts build.
 * `onPick(label)` makes bars and slices clickable (the canvas filters on it).
 */
export const ChartBlock = ({ chartType, data, onPick }) => {
  const ref = useRef(null);
  const onPickRef = useRef(onPick); onPickRef.current = onPick;
  const { t } = useTranslation('aiExplorer');
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !window.echarts || !data || !Array.isArray(data.labels)) { setUnavailable(true); return undefined; }
    const text = getComputedStyle(el).color;
    const isPie = chartType === 'pie' || chartType === 'doughnut';
    const values = Array.isArray(data.values) ? data.values : null;
    const datasets = Array.isArray(data.datasets) ? data.datasets : null;
    if (!isPie && !values && !datasets) { setUnavailable(true); return undefined; }

    const chart = window.echarts.init(el, null, { renderer: 'canvas' });
    const option = isPie ? {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { bottom: 0, textStyle: { color: text, fontSize: 11 } },
      series: [{
        type: 'pie', radius: chartType === 'doughnut' ? ['42%', '70%'] : '70%', center: ['50%', '45%'],
        data: data.labels.map((label, i) => ({ name: label, value: (values || [])[i], itemStyle: { color: PALETTE[i % PALETTE.length] } })),
        label: { color: text, fontSize: 12 },
      }],
      backgroundColor: 'transparent',
    } : {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: datasets ? { top: 0, textStyle: { color: text, fontSize: 11 } } : undefined,
      grid: { left: 8, right: 8, top: datasets ? 32 : 12, bottom: 8, containLabel: true },
      xAxis: { type: 'category', data: data.labels, axisLabel: { rotate: data.labels.length > 6 ? 30 : 0, color: text, fontSize: 11 }, axisLine: { lineStyle: { color: 'rgba(128,128,128,0.3)' } } },
      yAxis: { type: 'value', axisLabel: { color: text, fontSize: 11 }, splitLine: { lineStyle: { color: 'rgba(128,128,128,0.15)' } } },
      series: datasets
        ? datasets.map((ds, i) => ({ name: ds.label, data: ds.data || [], type: chartType === 'line' ? 'line' : 'bar', itemStyle: { color: PALETTE[i % PALETTE.length] }, smooth: true }))
        : [{ data: values, type: chartType === 'line' ? 'line' : 'bar', itemStyle: { color: (p) => PALETTE[p.dataIndex % PALETTE.length] }, smooth: true }],
      backgroundColor: 'transparent',
    };
    chart.setOption(option);
    chart.on('click', (p) => { if (onPickRef.current && p?.name) onPickRef.current(String(p.name)); });
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(el);
    return () => { ro.disconnect(); chart.dispose(); };
  }, [chartType, data]);

  if (unavailable) return <div className="sa3-state">{t('agent.chartUnavailable')}</div>;
  return <div className="sa3-chart"><div ref={ref} className="sa3-chart-box" role="img" aria-label={t('agent.chart')} /></div>;
};

const TableBlock = ({ table, t }) => (
  <div className="sa3-tbl">
    <div className="sa3-tbl-h"><span>{t('agent.table')}</span></div>
    <div className="sa3-tbl-scroll">
      <table>
        <thead><tr>{table.headers.map((h, i) => <th key={i}>{String(h)}</th>)}</tr></thead>
        <tbody>
          {table.rows.map((r, i) => <tr key={i}>{(Array.isArray(r) ? r : []).map((c, j) => <td key={j}>{c == null ? '' : String(c)}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  </div>
);

const STEP_ICON = { running: 'loader', done: 'check', error: 'x', held: 'pause' };

/** What the agent called, in order: collapsed to one line once the answer is in. */
const StepsBlock = ({ steps, streaming, t }) => {
  const [open, setOpen] = useState(false);
  const n = steps.length;
  const secs = stepsDuration(steps);
  const label = streaming ? t('agent.working', { count: n }) : t('agent.workedFor', { seconds: secs, count: n });
  return (
    <div className={`sa3-steps${streaming ? ' live' : ''}`}>
      <button type="button" className="sa3-steps-h" onClick={() => setOpen((o) => !o)} aria-expanded={open || streaming}>
        <FeatherIcon name={streaming ? 'loader' : 'check-circle'} size={14} /><span>{label}</span><FeatherIcon name={open || streaming ? 'chevron-up' : 'chevron-down'} size={14} />
      </button>
      {(open || streaming) && (
        <ol>
          {steps.map((s) => (
            <li key={s.id} className={`st-${s.status}`}>
              <FeatherIcon name={STEP_ICON[s.status] || 'circle'} size={13} />
              <code>{s.name}</code>
              {s.write && <em>{t('agent.needsApproval')}</em>}
              <span className="sa3-step-sum">{s.status === 'held' ? t('agent.stepHeld') : s.summary}</span>
              {typeof s.ms === 'number' && <small>{(s.ms / 1000).toFixed(1)}s</small>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

/** A write the agent wants to make. Nothing happens until Approve. */
const ApprovalCard = ({ a, busy, onApprove, onReject, t }) => (
  <div className={`sa3-approval ${a.status}`} role="group" aria-label={t('agent.needsApproval')}>
    <div className="sa3-approval-h"><FeatherIcon name="alert-circle" size={15} /><b>{t('agent.approvalTitle')}</b><code>{a.name}</code></div>
    <dl>
      {Object.entries(a.arguments || {}).map(([k, v]) => (
        <React.Fragment key={k}><dt>{k}</dt><dd>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd></React.Fragment>
      ))}
    </dl>
    {a.status === 'pending' && (
      <div className="sa3-approval-acts">
        <button type="button" className="sa3-txtbtn" onClick={onReject} disabled={busy}>{t('agent.reject')}</button>
        <button type="button" className="sa3-txtbtn primary" onClick={onApprove} disabled={busy}>{t('agent.approve')}</button>
      </div>
    )}
    {a.status === 'running' && <div className="sa3-approval-state">{t('agent.running')}</div>}
    {a.status === 'approved' && <div className="sa3-approval-state ok"><FeatherIcon name="check" size={13} />{t('agent.actionRan', { name: a.name })}</div>}
    {a.status === 'rejected' && <div className="sa3-approval-state">{t('agent.rejected')}</div>}
    {a.status === 'failed' && <div className="sa3-approval-state bad"><FeatherIcon name="x" size={13} />{a.result?.error || t('agent.actionFailed', { name: a.name })}</div>}
  </div>
);

/** The Save-to-dashboard menu: existing dashboards from the API, plus "new". */
const SaveMenu = ({ dashboards, onPick, onClose, t }) => {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  return (
    <div ref={ref} className="sa3-menu up" role="menu" aria-label={t('agent.saveToDashboard')}>
      <div className="sa3-mh">{t('agent.saveToDashboard')}</div>
      {dashboards.loading && <div className="sa3-mnote">{t('agent.loading')}</div>}
      {dashboards.error && <div className="sa3-mnote error">{t('agent.dashboardsUnavailable')}</div>}
      {!dashboards.loading && !dashboards.error && dashboards.list.length === 0 && <div className="sa3-mnote">{t('agent.noDashboards')}</div>}
      {dashboards.list.map((d) => (
        <button key={d.id} type="button" role="menuitem" className="sa3-mi" onClick={() => onPick(d.id)}>
          <FeatherIcon name="layout" size={14} />{d.title}
          {typeof d.widget_count === 'number' && <small>{t('agent.widgetCount', { count: d.widget_count })}</small>}
        </button>
      ))}
      <button type="button" role="menuitem" className="sa3-mi new" onClick={() => onPick(null)}>
        <FeatherIcon name="plus" size={14} />{t('agent.newDashboard')}
      </button>
    </div>
  );
};

/**
 * One turn. User turns are a bubble; agent turns carry the answer, any chart
 * or table, the action bar and follow-up chips.
 *
 * `canvas` (when the canvas is on) swaps "Save to dashboard" for a one-click
 * Pin: `{ pinned(m) → tile | null, pin(m), unpin(tileId) }`.
 */
export default function MessageItem({ m, isLastAgent, busy, dashboards, actions, canvas, picker }) {
  const { t } = useTranslation('aiExplorer');
  const [saveOpen, setSaveOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);     // null | 'positive' | 'negative'
  const [comment, setComment] = useState('');
  const [sent, setSent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pinning, setPinning] = useState(false);

  if (m.role === 'user') return <div className="sa3-u">{m.content}</div>;

  // A page notice: the greeting or a "you're now on X" row, with the page's
  // headline numbers as cards that ask their own question when clicked.
  if (m.ephemeral) {
    return (
      <div className="sa3-a sa3-notice">
        <span className="sa3-mark" aria-hidden="true"><FeatherIcon name="zap" size={15} /></span>
        <div className="sa3-ab">
          <MarkdownText>{m.content}</MarkdownText>
          {Array.isArray(m.contextCards) && m.contextCards.length > 0 && (
            <div className="sa3-ctx" aria-label={t('agent.pageSnapshot')}>
              {m.contextCards.map((c) => (
                <button key={c.label} type="button" className="sa3-ctx-card" onClick={() => c.query && actions.followUp(c.query)} disabled={!c.query || busy}>
                  <span className="sa3-ctx-ic" style={{ color: c.color || 'var(--ag-accent-text)' }}><FeatherIcon name={c.icon || 'activity'} size={14} /></span>
                  <span className="sa3-ctx-v">{c.value}</span>
                  <span className="sa3-ctx-l">{c.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const table = m.displayType === 'table' ? tableFromMessage(m) : null;
  // The API parses `data` out of the answer's own markdown table, so a block is
  // only drawn when the text does not already carry one; CSV covers both.
  const tableBlock = table && !/^\s*\|.*\|\s*$/m.test(m.content || '') ? table : null;
  const finished = !m.loading && !m.streaming;
  const showBody = m.content || !m.loading;

  const openSave = () => { setSaveOpen(true); dashboards.load(); };
  const pick = async (dashboardId) => {
    setSaving(true);
    try { await actions.saveWidget(m, dashboardId); setSaveOpen(false); } finally { setSaving(false); }
  };
  const pinnedTile = canvas ? canvas.pinned(m) : null;
  const togglePinned = async () => {
    setPinning(true);
    try { if (pinnedTile) await canvas.unpin(pinnedTile.id); else await canvas.pin(m); } finally { setPinning(false); }
  };
  const sendFeedback = async (type, text) => {
    const ok = await actions.feedback(m, type, text);
    if (ok) { setSent(type); setFeedback(null); setComment(''); }
  };

  return (
    <div className={`sa3-a${picker?.checked ? ' picked' : ''}`} data-mid={m.id}>
      {picker && finished && !m.error && m.content ? (
        <label className="sa3-pick" title={t('agent.pickThis')}>
          <input type="checkbox" checked={picker.checked} onChange={picker.toggle} aria-label={t('agent.pickThis')} />
        </label>
      ) : <span className="sa3-mark" aria-hidden="true"><FeatherIcon name="zap" size={15} /></span>}
      <div className="sa3-ab">
        {m.loading && !m.content && (
          <div className="sa3-thinking" aria-live="polite"><i /><i /><i /><span>{t('agent.thinking')}</span></div>
        )}
        {Array.isArray(m.steps) && m.steps.length > 0 && <StepsBlock steps={m.steps} streaming={!finished} t={t} />}
        {Array.isArray(m.notices) && m.notices.map((n, i) => (
          <div key={i} className="sa3-notice-line"><FeatherIcon name="info" size={13} />{n.code === 'tools_unavailable' ? t('agent.answeredWithoutTools', { reason: n.message }) : n.message}</div>
        ))}
        {showBody && m.content && !m.error && (
          <div>
            <MarkdownText>{m.content}</MarkdownText>
            {m.streaming && <span className="sa3-cursor" aria-hidden="true" />}
          </div>
        )}
        {m.error && (
          <div className="sa3-error" role="alert">
            <FeatherIcon name="alert-triangle" size={16} />
            <div style={{ flex: 1 }}>
              <div>{t(m.errorKey || 'agent.errorGeneric')}</div>
              {m.content && <div style={{ color: 'var(--ag-t2)', marginTop: 4, fontSize: 13 }}>{m.content}</div>}
            </div>
            {isLastAgent && !busy && <button type="button" onClick={() => actions.regenerate(m)}>{t('agent.retry')}</button>}
          </div>
        )}
        {m.stopped && <div style={{ color: 'var(--ag-t3)', fontSize: 12.5 }}>{t('agent.stopped')}</div>}
        {Array.isArray(m.approvals) && m.approvals.map((a) => (API_KEY_TOOLS.includes(a.name) ? (
          <Suspense key={a.id} fallback={<div className="sa3-approval-state">{t('agent.loading')}</div>}>
            <ApiKeyApproval a={a} busy={busy} onResolve={(patch) => actions.resolve(m, a, patch)} onReject={() => actions.reject(m, a)} />
          </Suspense>
        ) : ALERT_RULE_TOOLS.includes(a.name) ? (
          <Suspense key={a.id} fallback={<div className="sa3-approval-state">{t('agent.loading')}</div>}>
            {/* Approve sends the arguments as the person edited them, through the same tool call. */}
            <AlertRuleApproval a={a} busy={busy} onApprove={(edited) => actions.approve(m, { ...a, arguments: edited })} onReject={() => actions.reject(m, a)} />
          </Suspense>
        ) : <ApprovalCard key={a.id} a={a} busy={busy} onApprove={() => actions.approve(m, a)} onReject={() => actions.reject(m, a)} t={t} />))}
        {finished && m.displayType === 'chart' && m.data && <ChartBlock chartType={m.chartType} data={m.data} />}
        {finished && tableBlock && <TableBlock table={tableBlock} onCsv={actions.downloadCsv} t={t} />}
        {m.dashboard && (
          <div className="sa3-dashcard">
            <FeatherIcon name="layout" size={18} />
            <div><b>{m.dashboard.title}</b><small>{t('agent.dashboardGenerated', { count: m.dashboard.count })}</small></div>
            <Link to={`/ai-explorer/dashboards/${m.dashboard.id}`}>{t('agent.viewDashboard')}</Link>
          </div>
        )}

        {finished && !m.error && m.content && (
          <div className="sa3-acts" role="toolbar" aria-label={t('agent.responseActions')}>
            <button type="button" className="sa3-ibtn" onClick={() => actions.copy(m)} aria-label={t('agent.copy')} title={t('agent.copy')}><FeatherIcon name="copy" size={15} /></button>
            <button type="button" className={`sa3-ibtn${sent === 'positive' ? ' on' : ''}`} onClick={() => sendFeedback('positive')} aria-label={t('agent.helpful')} title={t('agent.helpful')} aria-pressed={sent === 'positive'}><FeatherIcon name="thumbs-up" size={15} /></button>
            <button type="button" className={`sa3-ibtn${sent === 'negative' ? ' on' : ''}`} onClick={() => setFeedback(feedback === 'negative' ? null : 'negative')} aria-label={t('agent.notHelpful')} title={t('agent.notHelpful')} aria-pressed={sent === 'negative'}><FeatherIcon name="thumbs-down" size={15} /></button>
            {isLastAgent && (
              <button type="button" className="sa3-ibtn" onClick={() => actions.regenerate(m)} disabled={busy} aria-label={t('agent.regenerate')} title={t('agent.regenerate')}><FeatherIcon name="refresh-cw" size={15} /></button>
            )}
            <span className="sa3-sep" />
            {table && (
              <button type="button" className="sa3-txtbtn" onClick={() => actions.downloadCsv(table)}><FeatherIcon name="download" size={14} />{t('agent.downloadCsv')}</button>
            )}
            {canvas ? (
              <button type="button" className={`sa3-txtbtn${pinnedTile ? ' on' : ''}`} onClick={togglePinned} disabled={pinning} aria-pressed={!!pinnedTile} title={pinnedTile ? t('agent.unpinFromCanvas') : t('agent.pinToCanvas')}>
                <FeatherIcon name={pinnedTile ? 'check-square' : 'plus-square'} size={14} />{pinnedTile ? t('agent.pinnedToCanvas') : t('agent.pinToCanvas')}
              </button>
            ) : (
              <div style={{ position: 'relative' }}>
                <button type="button" className="sa3-txtbtn" onClick={openSave} disabled={saving}><FeatherIcon name="layout" size={14} />{t('agent.saveToDashboard')}</button>
                {saveOpen && <SaveMenu dashboards={dashboards} onPick={pick} onClose={() => setSaveOpen(false)} t={t} />}
              </div>
            )}
          </div>
        )}
        {feedback === 'negative' && (
          <div className="sa3-feedback">
            <label htmlFor={`fb-${m.id}`} style={{ fontSize: 13, color: 'var(--ag-t2)' }}>{t('agent.feedbackPrompt')}</label>
            <textarea id={`fb-${m.id}`} value={comment} onChange={(e) => setComment(e.target.value)} />
            <div className="sa3-feedback-acts">
              <button type="button" className="sa3-txtbtn" onClick={() => setFeedback(null)}>{t('agent.cancel')}</button>
              <button type="button" className="sa3-txtbtn primary" onClick={() => sendFeedback('negative', comment)}>{t('agent.submit')}</button>
            </div>
          </div>
        )}
        {finished && isLastAgent && !busy && Array.isArray(m.suggestions) && m.suggestions.length > 0 && (
          <div className="sa3-fu" aria-label={t('agent.suggestedFollowUps')}>
            {m.suggestions.map((s) => (
              <button key={s} type="button" onClick={() => actions.followUp(s)}><FeatherIcon name="arrow-right" size={13} />{s}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
